/**
 * Password validation utility for enforcing strong password requirements
 */

export interface PasswordStrength {
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  hasMinLength: boolean;
  isValid: boolean;
}

/**
 * Validates password strength based on multiple criteria
 * @param password - The password to validate
 * @returns PasswordStrength object with validation results
 */
export const validatePasswordStrength = (password: string): PasswordStrength => {
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  const hasMinLength = password.length >= 8;

  return {
    hasUpperCase,
    hasLowerCase,
    hasNumber,
    hasSpecialChar,
    hasMinLength,
    isValid: hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && hasMinLength
  };
};

/**
 * Gets a user-friendly error message for password validation
 * @param strength - The password strength object
 * @returns Error message string
 */
export const getPasswordErrorMessage = (strength: PasswordStrength): string => {
  const missing: string[] = [];
  
  if (!strength.hasMinLength) missing.push('at least 8 characters');
  if (!strength.hasUpperCase) missing.push('an uppercase letter');
  if (!strength.hasLowerCase) missing.push('a lowercase letter');
  if (!strength.hasNumber) missing.push('a number');
  if (!strength.hasSpecialChar) missing.push('a special character (!, @, #, $, etc.)');
  
  if (missing.length === 0) return '';
  
  return `Password must contain ${missing.join(', ')}.`;
};

